import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '@utils/axios';
import { apiInternalPriceRulePath } from 'routes';
import SearchDropdown from './SearchDropdown';

const priceListInput = document.querySelector('[name*="invoice[price_list_id]"]');

function appendQbIcon(afterEl) {
  const img = document.createElement('img');
  img.height = 16;
  img.width = 16;
  img.src = '/qbo.svg';
  img.className = 'custom-icon--qbo mt-2';
  img.title = 'QuickBooks';
  afterEl.after(img);
}

function MaterialsServicesSelector({
  invoicingLineItem,
  proposedCorrection: initialProposedCorrection,
  priceRule: initialPriceRule,
  objectName,
  priceListId: initialPriceListId,
  // IP-12975: the QuickBooks badge follows the integration that is on (see QuickBooksHelper).
  showQboIcons,
  showQbdIcons
}) {
  const [proposedCorrection, setProposedCorrection] = useState({
    id: '',
    price: 0,
    description: '',
    ...(initialProposedCorrection || {})
  });
  const [priceListId, setPriceListId] = useState(priceListInput?.value ?? initialPriceListId ?? '');
  const [priceRule, setPriceRule] = useState(initialPriceRule);
  const [price, setPrice] = useState(invoicingLineItem.unitPrice);
  const [firstTimeRender, setFirstTimeRender] = useState(true);
  const input = useRef();

  useEffect(() => {
    if (!priceListInput) return undefined;

    const onPriceListChange = function () {
      setPriceListId(this.value);
    };

    priceListInput.addEventListener('change', onPriceListChange);

    // `priceListInput` is module-scoped and shared by every line-item row, so without this
    // each mounted row leaves another listener behind.
    return () => priceListInput.removeEventListener('change', onPriceListChange);
  }, []);

  // const isInvoicingLineItemChanged = useMemo(
  //   () => invoicingLineItem.proposedCorrectionId?.toString() !== proposedCorrection.id?.toString(),
  //   [proposedCorrection]
  // );

  // const isPriceListChanged = useMemo(() => priceListId.toString() !== initialPriceListId?.toString(), [priceListId]);

  useEffect(() => {
    if (firstTimeRender) {
      setFirstTimeRender(false);
      return;
    }

    if (!priceListId || !proposedCorrection?.id) {
      setPriceRule({});
      setPrice(0);
      return;
    }

    // it might be used for keeping old values if needed
    // if (!isInvoicingLineItemChanged && !isPriceListChanged) {
    //   setPriceRule(initialPriceRule);
    //   setPrice(invoicingLineItem.unitPrice);
    //   return;
    // }

    api
      .get(apiInternalPriceRulePath({ price_list_id: priceListId, proposed_correction_id: proposedCorrection.id }))
      .then(({ data }) => {
        setPriceRule(data.price_rule);
        setPrice(data.price);
      });
  }, [priceListId, proposedCorrection]);

  useEffect(() => {
    if (firstTimeRender) return;

    setRelatedFormFields();
    input.current.dispatchEvent(new Event('change', { bubbles: true }));
  }, [proposedCorrection]);

  useEffect(() => {
    if (firstTimeRender) return;

    document.querySelector(`[name*="${objectName}[unit_price]"]`).value = Math.round(price * 100) / 100;

    update_total();
  }, [price]);

  const clearRelatedFormFields = () => {
    document.querySelector(`[name*="${objectName}[qbd_id]"]`).value = '';
    document.querySelector(`[name*="${objectName}[qbd_id]"] + img`)?.remove();

    document.querySelector(`[name*="${objectName}[description]"]`).value = '';
    document.querySelector(`[name*="${objectName}[unit_price]"]`).value = '';
    document.querySelector(`.invoice-is-taxable[name*="${objectName}[is_taxable]"]`).checked = false;

    // Clear QB Class dropdown
    const qbClassSelect = document.querySelector(`[name*="${objectName}[qb_class_ref_id]"]`);
    if (qbClassSelect) {
      qbClassSelect.value = '';
      $(qbClassSelect).trigger('chosen:updated');
    }
  };

  const setRelatedFormFields = () => {
    clearRelatedFormFields();

    if (proposedCorrection.description_other) {
      document.querySelector(`[name*="${objectName}[description]"]`).value = proposedCorrection.description_other;
    }

    document.querySelector(`.invoice-is-taxable[name*="${objectName}[is_taxable]"]`).checked =
      proposedCorrection.taxable;

    if (proposedCorrection.qboId) {
      document.querySelector(`[name*="${objectName}[qbo_id]"]`).value = proposedCorrection.qboId;
      if (showQboIcons) appendQbIcon(document.querySelector(`[name*="${objectName}[qbo_id]"]`));
    } else if (proposedCorrection.qbdId) {
      document.querySelector(`[name*="${objectName}[qbd_id]"]`).value = proposedCorrection.qbdId;
      if (showQbdIcons) appendQbIcon(document.querySelector(`[name*="${objectName}[qbd_id]"]`));
    }

    // Set QB Class if present
    const qbClassSelect = document.querySelector(`[name*="${objectName}[qb_class_ref_id]"]`);
    if (qbClassSelect) {
      if (proposedCorrection.qbClassRefId) {
        qbClassSelect.value = proposedCorrection.qbClassRefId;
        $(qbClassSelect).trigger('chosen:updated');
      } else {
        qbClassSelect.value = '';
        $(qbClassSelect).trigger('chosen:updated');
      }
    }
  };

  const handleSelectionClear = () => {
    setProposedCorrection({ id: '', price: 0, description: '' });

    clearRelatedFormFields();

    update_total();
  };

  return (
    <>
      <input type="hidden" ref={input} name={`${objectName}[proposed_correction_id]`} value={proposedCorrection?.id} />
      <input type="hidden" ref={input} name={`${objectName}[product]`} value={proposedCorrection?.description} />
      <input type="hidden" name={`${objectName}[price_rule_id]`} value={priceRule?.id ?? ''} />
      {proposedCorrection?.id && (
        <div
          className="qmb-input --x-full"
          style={{ display: 'flex', padding: '8px', boxSizing: 'border-box', alignItems: 'center', width: '24rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <small>{proposedCorrection.type}</small>
            <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '.5rem', alignItems: 'baseline' }}>
              {proposedCorrection.description}
              <button type="button" className="qmb-control" style={{ padding: '3px 6px', borderRadius: '10px' }}>
                {/* <small>{priceRule?.asApplicable ?? 'No Price Rule'}</small> */}
              </button>
            </div>
          </div>
          <button type="button" className="qmb-control--icon" onClick={handleSelectionClear}>
            <i className="fa-light fa-xmark" />
          </button>
        </div>
      )}

      <SearchDropdown proposedCorrection={proposedCorrection} setProposedCorrection={setProposedCorrection} />
    </>
  );
}

MaterialsServicesSelector.propTypes = {
  invoicingLineItem: PropTypes.object.isRequired,
  proposedCorrection: PropTypes.object,
  objectName: PropTypes.string.isRequired,
  priceRule: PropTypes.object.isRequired,
  priceListId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showQboIcons: PropTypes.bool,
  showQbdIcons: PropTypes.bool
};

MaterialsServicesSelector.defaultProps = {
  showQboIcons: true,
  showQbdIcons: true,
  proposedCorrection: {
    id: '',
    price: 0
  },
  priceListId: null
};

export default MaterialsServicesSelector;
